<!-- Adapted from YesPlayMusic v0.4.11 (MIT), upstream TrackListItem.vue.
     Original song-row structure and SCSS retained; canonical tracks and playback use the shared runtime. -->
<template>
  <div
    class="track track-row"
    :class="trackClass"
    :title="track.reason || ''"
    :data-track-row="track.id"
    :data-track-id="track.id"
  >
    <div v-if="!isAlbum" class="track-cover">
      <img :src="imgUrl" alt="" loading="lazy" :class="{ hover: focus }" @error="onImageError" />
      <component :is="buttonComponent" v-bind="saltButtonProps" type="button" class="cover-play" data-play :aria-label="'播放 ' + track.name" @click.stop="playTrack" @dblclick.stop>
        <svg-icon :icon-class="isPlaying && state.isPlaying ? 'volume' : 'play'" />
      </component>
    </div>
    <div v-if="showOrderNumber" class="no">
      <component :is="buttonComponent" v-bind="saltButtonProps" type="button" :aria-label="'播放 ' + track.name" @click.stop="playTrack" @dblclick.stop>
        <svg-icon v-if="focus || isPlaying" :icon-class="isPlaying && state.isPlaying ? 'volume' : 'play'" style="height: 14px; width: 14px" />
        <span v-else>{{ trackNo }}</span>
      </component>
    </div>
    <div class="title-and-artist">
      <div class="container">
        <div class="title">
          {{ track.name }}
          <span v-if="isSubTitle" :title="subTitle" class="sub-title">({{ subTitle }})</span>
          <span v-if="isAlbum" class="featured">
            <ArtistsInLine :artists="artists" :exclude="albumArtistName" prefix="-" />
          </span>
          <span v-if="isAlbum && (track.mark & 1048576) === 1048576" class="explicit-symbol"><ExplicitSymbol /></span>
        </div>
        <div v-if="!isAlbum" class="artist">
          <span v-if="(track.mark & 1048576) === 1048576" class="explicit-symbol before-artist"><ExplicitSymbol :size="15" /></span>
          <ArtistsInLine :artists="artists" />
        </div>
      </div>
      <div></div>
    </div>
    <div v-if="showAlbumName" class="album">
      <router-link v-if="album.id" :to="{name: 'album', params: {id: String(album.id)}}">{{ album.name }}</router-link>
      <span v-else>{{ album.name }}</span>
      <div></div>
    </div>
    <div class="actions">
      <component :is="buttonComponent" v-if="showLikeButton" v-bind="saltButtonProps" type="button" :aria-label="(isLiked ? '取消喜欢 ' : '喜欢 ') + track.name" :aria-pressed="String(isLiked)" @click.stop="likeThisSong" @dblclick.stop>
        <svg-icon :icon-class="isLiked ? 'heart-solid' : 'heart'" />
      </component>
      <component :is="buttonComponent" v-bind="saltButtonProps" type="button" :aria-label="'更多操作 ' + track.name" aria-haspopup="menu" @click.stop="$emit('menu', $event)" @dblclick.stop>
        <svg-icon icon-class="more" />
      </component>
    </div>
    <div v-if="showTrackTime" class="time">{{ formatTime(track.dt) }}</div>
    <div v-if="track.playCount" class="count">{{ track.playCount }}</div>
  </div>
</template>

<script>
import ArtistsInLine from './ArtistsInLine.vue';
import ExplicitSymbol from './ExplicitSymbol.vue';
import SvgIcon from './SvgIcon.vue';
import { runtime } from '../bridge';
import { trackArtistItems } from '../artist-navigation';
import { albumRouteId } from '../album-identity';
import { workspace } from '../workspace';
import SaltButton from '../salt/ui/SaltButton.vue';

export default {
  name: 'TrackListItem',
  components: { ArtistsInLine, ExplicitSymbol, SvgIcon, SaltButton },
  props: {
    trackProp: { type: Object, required: true },
    trackNo: Number,
    highlightPlayingTrack: { type: Boolean, default: true },
  },
  data() { return { state: runtime.state, workspace }; },
  computed: {
    buttonComponent() { return this.workspace.appearance === 'salt' ? 'SaltButton' : 'button'; },
    saltButtonProps() { return this.workspace.appearance === 'salt' ? { styleType: 'plain', iconOnly: true } : {}; },
    track() {
      const t = this.trackProp;
      return {
        ...t,
        name: t.title,
        ar: trackArtistItems(t),
        al: {
          name: t.album || '未知专辑',
          id: albumRouteId(t),
          picUrl: runtime.cover(t),
        },
        dt: t.durationMs || 0,
        alia: t.alia || [],
        reason: t.availability === 'unavailable' ? '本地音频未授权，请重新导入原文件后播放' : '',
        playable: t.availability !== 'unavailable',
      };
    },
    imgUrl() { return this.track.al.picUrl; },
    artists() { return this.track.ar; },
    album() { return this.track.al; },
    albumArtistName() { return this.$parent.albumObject?.artist?.name || this.$parent.albumObject?.artists?.[0]?.name || ''; },
    subTitle() { return this.track.alia?.[0] || this.track.tns?.[0] || ''; },
    type() { return this.$parent.type || 'playlist'; },
    isAlbum() { return this.type === 'album'; },
    isSubTitle() { return Boolean(this.subTitle && this.subTitle !== this.track.name); },
    isLiked() { return this.state.favorites.includes(this.track.id); },
    isPlaying() { return this.state.currentId === this.track.id; },
    trackClass() {
      return [this.type, {
        disable: !this.track.playable,
        playing: this.isPlaying && this.highlightPlayingTrack,
        focus: this.focus,
      }];
    },
    // Hover is deliberately left to the browser's :hover state. Keeping it in
    // component data can leave several virtualized rows highlighted when a
    // wheel scroll replaces the row under a stationary pointer before a
    // matching mouseleave event is delivered.
    isMenuOpened() { return this.$parent.rightClickedTrack === this.trackProp; },
    focus() { return this.isMenuOpened; },
    showLikeButton() { return this.type !== 'tracklist' && this.type !== 'cloudDisk'; },
    showOrderNumber() { return this.isAlbum; },
    showAlbumName() { return this.type !== 'album' && this.type !== 'tracklist'; },
    showTrackTime() { return this.type !== 'tracklist'; },
  },
  methods: {
    formatTime: runtime.formatTime,
    playTrack() { this.$emit('play'); },
    likeThisSong() { runtime.registerTracks([this.trackProp]); runtime.toggleFavorite(this.track.id); },
    onImageError(event) {
      const fallback = runtime.cover();
      if (event.target.src !== fallback) event.target.src = fallback;
    },
  },
};
</script>

<style lang="scss" scoped>

button {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 8px;
  background: transparent;
  border-radius: 25%;
  transition: transform 0.2s;
  .svg-icon {
    height: 16px;
    width: 16px;
    color: var(--color-primary);
  }
  &:hover {
    transform: scale(1.12);
  }
  &:active {
    transform: scale(0.96);
  }
}

.track {
  display: flex;
  align-items: center;
  padding: 8px;
  border-radius: 12px;
  user-select: none;

  .no {
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 8px;
    margin: 0 20px 0 10px;
    width: 12px;
    color: var(--color-text);
    cursor: default;
    span {
      opacity: 0.58;
    }
  }

  .explicit-symbol {
    opacity: 0.28;
    color: var(--color-text);
    .svg-icon {
      margin-bottom: -3px;
    }
  }

  .explicit-symbol.before-artist {
    .svg-icon {
      margin-bottom: -3px;
    }
  }

  img {
    border-radius: 8px;
    height: 46px;
    width: 46px;
    margin-right: 20px;
    border: 1px solid rgba(0, 0, 0, 0.04);
    cursor: pointer;
  }

  img.hover {
    filter: drop-shadow(100 200 0 black);
  }

  .title-and-artist {
    flex: 1;
    display: flex;
    .container {
      display: flex;
      flex-direction: column;
    }
    .title {
      font-size: 18px;
      font-weight: 600;
      color: var(--color-text);
      cursor: default;
      padding-right: 16px;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 1;
      overflow: hidden;
      word-break: break-all;
      .featured {
        margin-right: 2px;
        font-weight: 500;
        font-size: 14px;
        opacity: 0.72;
      }
      .sub-title {
        color: #7a7a7a;
        opacity: 0.7;
        margin-left: 4px;
      }
    }
    .artist {
      margin-top: 2px;
      font-size: 13px;
      opacity: 0.68;
      color: var(--color-text);
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 1;
      overflow: hidden;
      a {
        span {
          margin-right: 3px;
          opacity: 0.8;
        }
        &:hover {
          text-decoration: underline;
          cursor: pointer;
        }
      }
    }
  }
  .album {
    flex: 1;
    display: flex;
    font-size: 16px;
    opacity: 0.88;
    color: var(--color-text);
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
  }
  .time,
  .count {
    font-size: 16px;
    width: 50px;
    cursor: default;
    display: flex;
    justify-content: flex-end;
    margin-right: 10px;
    font-variant-numeric: tabular-nums;
    opacity: 0.88;
    color: var(--color-text);
  }
  .count {
    font-weight: bold;
    font-size: 22px;
    line-height: 22px;
  }
}

.track.focus,
.track:hover,
.track:focus-within {
  transition: all 0.3s;
  background: var(--color-secondary-bg);
}

.track.disable {
  img {
    filter: grayscale(1) opacity(0.6);
  }
  .title,
  .artist,
  .album,
  .time,
  .no,
  .featured {
    opacity: 0.28 !important;
  }
  &:hover {
    background: none;
  }
}

.track.tracklist {
  img {
    height: 36px;
    width: 36px;
    border-radius: 6px;
    margin-right: 14px;
    cursor: pointer;
  }
  .title {
    font-size: 16px;
  }
  .artist {
    font-size: 12px;
  }
}

.track.album {
  height: 32px;
}

.actions {
  width: 80px;
  display: flex;
  justify-content: flex-end;
}

.track.playing {
  background: var(--color-primary-bg);
  color: var(--color-primary);
  .title,
  .album,
  .time,
  .title-and-artist .sub-title {
    color: var(--color-primary);
  }
  .title .featured,
  .artist,
  .explicit-symbol,
  .count {
    color: var(--color-primary);
    opacity: 0.88;
  }
  .no span {
    color: var(--color-primary);
    opacity: 0.78;
  }
}

// The explicit play affordance makes the upstream double-click row usable on touch and keyboard.
.track-cover { position: relative; flex: 0 0 46px; height: 46px; margin-right: 20px; }
.track .track-cover img { display: block; margin: 0; }
.track .cover-play {
  position: absolute; inset: 0; border-radius: 8px; opacity: 0;
  background: rgba(0, 0, 0, .38); transition: opacity .15s;
  .svg-icon { color: white; }
}
.track.focus .cover-play, .track:hover .cover-play, .track-cover:focus-within .cover-play { opacity: 1; }
.track.tracklist .track-cover { flex-basis: 36px; height: 36px; margin-right: 14px; }
.track.tracklist .actions { width: 32px; }
.track .title-and-artist, .track .container { min-width: 0; }
.track .actions, .track .time, .track .no { flex-shrink: 0; }
.track button:focus-visible { outline: 2px solid var(--color-primary); outline-offset: 2px; }
.track .salt-ui-button ::v-deep(.label) { display:inline-flex; align-items:center; justify-content:center; width:100%; height:100%; }
.track .salt-ui-button ::v-deep(.svg-icon) { display:block; width:16px; height:16px; flex-shrink:0; color:var(--salt-accent); }
.track .cover-play.salt-ui-button ::v-deep(.svg-icon) { color:white; }
.track .no .salt-ui-button ::v-deep(.label) { opacity:1; }
@media (hover: none) { .track .cover-play { opacity: 1; } }
@media (max-width: 720px) {
  .track .album { display: none; }
  .track .title-and-artist .title { font-size: 16px; padding-right: 4px; }
  .track .time { width: 40px; font-size: 13px; margin-right: 0; }
  .track .track-cover { margin-right: 12px; }
  .track .actions { width: 68px; }
}
</style>

<style lang="scss">
/* SaltUI Main14/Sub12, Item spacing and AlphaIndication. YPM stays above. */
#app.salt-shell .track-list .track {
  min-height:56px;padding:8px 12px;border-radius:4px;
  .title-and-artist .title{font-size:14px;font-weight:400;line-height:21px;}
  .title-and-artist .artist{font-size:12px;line-height:18px;color:var(--salt-subtext);opacity:1;}
  .album,.time{font-size:12px;line-height:18px;}
  .no{font-size:12px;margin:0 12px 0 0;min-width:20px;}
  .track-cover{flex-basis:36px;width:36px;height:36px;margin-right:12px;}
  .track-cover img{width:36px;height:36px;border-radius:4px;filter:none;}
  .cover-play{border-radius:4px;}
  .actions{width:72px;gap:4px;}
  &.tracklist .actions{width:32px;}
  button{width:32px;height:32px;padding:6px;border-radius:0;box-shadow:none;transform:none;}
  button:active{opacity:.3;transform:none;}
  button:focus-visible{outline:none;box-shadow:inset 0 -1px var(--salt-accent);}
  .track-cover .cover-play{width:36px;height:36px;}
   &.focus,&:hover,&:focus-within{background:var(--salt-hover-overlay);transition:none;}
   &.disable:hover,&.disable:focus-within{background:none;}
  &.playing{background:var(--color-primary-bg);}
  &.album{height:auto;min-height:40px;}
}

/* Shared song rows keep real play/menu actions on phones. The secondary
   columns yield space before the controls' touch targets are reduced. */
#app.salt-mobile-layout .track-list .track.track-row {
  min-height:64px; height:64px; padding:8px;
  .album,.time,.count { display:none; }
  .track-cover { flex:0 0 44px; width:44px; height:44px; margin-right:8px; }
  .track-cover img { width:36px; height:36px; margin:4px; }
  .track-cover .cover-play { width:44px; min-width:44px; height:44px; min-height:44px; }
  .no { flex:0 0 44px; width:44px; min-width:44px; margin:0 8px 0 0; }
  .actions { width:88px; gap:0; }
  &.tracklist .actions { width:44px; }
  button { width:44px; min-width:44px; height:44px; min-height:44px; padding:12px; }
  .title-and-artist { flex:1; min-width:0; }
  .title-and-artist .title { padding-right:4px; }
}
</style>
